import { toast } from 'sonner';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/lib/store/auth-store';
import { RouterLink } from '@/components/router/router-link';
import { useNavigate, createFileRoute } from '@tanstack/react-router';
import { authControllerResetPassword } from '@/apis/backend/generated/auth/auth';

import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Box,
  Link,
  Stack,
  Button,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
} from '@mui/material';

import { paths } from 'src/routes/paths';

export const Route = createFileRoute('/auth/reset-password')({
  validateSearch: (search: Record<string, unknown>) => ({
    token: search.token as string | undefined,
  }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { token } = Route.useSearch();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // No token in URL -- show error immediately
  if (!token) {
    return (
      <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 700, letterSpacing: '-0.02em' }}>
            {t('resetPassword.expiredTitle')}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('resetPassword.expiredBody')}
          </Typography>

          <Button
            fullWidth
            size="large"
            variant="contained"
            component={RouterLink}
            href={paths.auth.forgotPassword}
            sx={{ mb: 2, py: 1.4 }}
          >
            {t('resetPassword.requestNew')}
          </Button>

          <Link component={RouterLink} href={paths.auth.signIn} sx={{ cursor: 'pointer' }}>
            <Typography variant="body2">{t('links.backToSignIn')}</Typography>
          </Link>
        </Box>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      toast.error(t('toasts.fillAllFields'));
      return;
    }

    if (password.length < 6) {
      toast.error(t('toasts.passwordTooShort'));
      return;
    }

    if (password !== confirmPassword) {
      toast.error(t('toasts.passwordsMismatch'));
      return;
    }

    setLoading(true);

    try {
      const { accessToken, refreshToken } = await authControllerResetPassword({ token, password });

      // Store tokens and fetch user
      await useAuthStore.getState().setTokensAndFetchUser(accessToken, refreshToken);

      toast.success(t('resetPassword.success'));
      navigate({ to: paths.dashboard.root });
    } catch (err: any) {
      console.error('Reset password error:', err);
      const message = err?.message || t('resetPassword.error');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Typography variant="h4" sx={{ mb: 0.5, fontWeight: 700, letterSpacing: '-0.02em' }}>
        {t('resetPassword.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        {t('resetPassword.subtitle')}
      </Typography>

      <Stack component="form" spacing={2.5} onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label={t('fields.newPassword')}
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={loading}
          autoComplete="new-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          label={t('fields.confirmPassword')}
          type={showConfirmPassword ? 'text' : 'password'}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={loading}
          autoComplete="new-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button fullWidth size="large" type="submit" variant="contained" disabled={loading} sx={{ py: 1.4 }}>
          {loading ? t('resetPassword.submitting') : t('resetPassword.submit')}
        </Button>
      </Stack>

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Link href={paths.auth.signIn} sx={{ cursor: 'pointer' }}>
          <Typography variant="body2">{t('links.backToSignIn')}</Typography>
        </Link>
      </Box>
    </>
  );
}
